import { all, put, takeLatest } from 'redux-saga/effects';
import { CloudActions } from '@portal/portal-core';
import { acceptInviteSaga, dismissInviteSaga } from './invite-saga';
import { authcodeRedirectSaga } from './authcode-saga';
import { updateUserProfileSaga, changeUserPasswordSaga } from './user-profile-saga';
import { fetchRolesSaga } from './fetch-roles-saga';
import * as Types from '../types';

// Minimal shape of the user client config threaded through every watcher into
// the still-untyped `.js` sagas that read `baseApiUrl` / `clientStorePath` / `getAuthToken`.
interface UserClientConfig {
  baseApiUrl?: string;
  clientStorePath?: string;
  getAuthToken?: () => string | Promise<string>;
}

// Uniform watcher-boundary signature for the worker sagas. Each worker module is
// typed independently (stricter config shapes with required `baseApiUrl`, action
// shapes that don't extend redux-saga's `Action`), which `takeLatest` cannot
// reconcile; casting to this shared shape erases those differences without
// changing runtime behavior.
type UserClientWorker = (
  config: UserClientConfig,
  action: { type: string; payload?: unknown },
) => Generator<unknown, void, unknown>;

/* istanbul ignore next reason: no saga watch tests */
export function* watchAcceptInviteSaga(
  config: UserClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(Types.ACCEPT_INVITE, acceptInviteSaga as unknown as UserClientWorker, config);
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchdismissInviteSaga(): Generator<unknown, void, unknown> {
  yield takeLatest(Types.DISMISS_INVITE, dismissInviteSaga);
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchAuthcodeRedirectSaga(
  config: UserClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.CLIENT_AUTHCODE_REDIRECT,
    authcodeRedirectSaga as unknown as UserClientWorker,
    config,
  );
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchUpdateUserProfileSaga(
  config: UserClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.UPDATE_USER_PROFILE,
    updateUserProfileSaga as unknown as UserClientWorker,
    config,
  );
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchChangeUserPasswordSaga(
  config: UserClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.CHANGE_USER_PASSWORD,
    changeUserPasswordSaga as unknown as UserClientWorker,
    config,
  );
}

/**
 * Roles are fetched once per authenticated session rather than on the userSync
 * poll cycle (contrast with products — see cloud-middlewares.ts
 * authCheckForUpdates, which now re-fetches products on every poll tick so
 * entitlement changes show up without a page reload). Roles are safe to fetch
 * once today only because the sole role, 'verifier', is sourced from the
 * static VERIFICATION_USER_IDS env var — it cannot change without a backend
 * redeploy, so there is nothing for a mid-session poll to pick up.
 *
 * THIS WILL NEED TO CHANGE once a role can change at runtime (e.g. a
 * database-backed role granted/revoked by an admin action). At that point,
 * polling roles here too is the easy option, but this codebase will likely
 * already have a push-based subscription mechanism for userSync/adminSync by
 * then (see the FJCLOUD-4420 code review notes) — prefer wiring roles into
 * that push mechanism over bolting on another poll.
 *
 * START_CLOUD_WATCHER fires exactly once per session: either from App.tsx's
 * boot-time check for an existing valid JWT (browser refresh / new tab), or
 * from the sign-in saga right after a fresh login. Unlike SIGN_IN_SUCCESS —
 * which cloudWatcher's poll loop re-dispatches on every cycle to refresh the
 * header/avatar — START_CLOUD_WATCHER is never re-dispatched by the poll loop
 * itself, so this only fetches roles the one time a session actually starts.
 */
/* istanbul ignore next reason: no saga watch tests */
export function* watchFetchRolesOnSessionStart(): Generator<unknown, void, unknown> {
  yield takeLatest(CloudActions.START_CLOUD_WATCHER, function* () {
    yield put({ type: Types.FETCH_USER_ROLES });
  });
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchFetchRolesSaga(
  config: UserClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.FETCH_USER_ROLES,
    fetchRolesSaga as unknown as UserClientWorker,
    config,
  );
}

// export wrapper function to capture client config for sagas to read from
/* istanbul ignore next reason: no rootSaga tests */
export default (config: UserClientConfig = {}) =>
  // return our rootSaga
  function* rootSaga(): Generator<unknown, void, unknown> {
    yield all([
      // import all additional watcher sagas here
      watchAcceptInviteSaga(config),
      watchdismissInviteSaga(),
      watchAuthcodeRedirectSaga(config),
      watchUpdateUserProfileSaga(config),
      watchChangeUserPasswordSaga(config),
      watchFetchRolesSaga(config),
      watchFetchRolesOnSessionStart(),
    ]);
  };
